import { Fragment, useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Collapse,
    FormControlLabel,
    InputAdornment,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import IndeterminateCheckBoxOutlinedIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { css } from '@emotion/react';

import { useSessionStore } from 'session/useSessionStore';
import { IS3File } from '../models/interfaces/IS3File';
import { IS3ListFilesResponse } from '../models/interfaces/IS3ListFilesResponse';
import { IWizardFile } from '../models/interfaces/IWizardFile';
import { QueueType } from '../models/enums/QueueType';
import { useListS3Files } from '../api/useQueueS3Controller';
import { getQueueTypeHandler } from '../queueType/QueueTypeRegistry';

const sftpPathStyle = css({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    gap: 12,
    '& .sftp-header': {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    '& .sftp-tree-container': {
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
    },
    '& .sftp-tree-loading': {
        display: 'flex',
        justifyContent: 'center',
        padding: 32,
    },
    '& .sftp-tree-empty': {
        padding: '24px 16px',
    },
});

const getFolderName = (folderPath: string): string => folderPath.split('/').pop() ?? folderPath;

const formatSize = (bytes: number): string => {
    if (bytes < 1024) {
        return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} KB`;
    }
    if (bytes < 1024 * 1024 * 1024) {
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleString();
};

interface ISftpFileNodeProps {
    file: IS3File;
    depth: number;
    selectedFiles: Map<string, IS3File>;
    onToggleFile: (file: IS3File) => void;
    disabled?: boolean;
}

function SftpFileNode({ file, depth, selectedFiles, onToggleFile, disabled }: ISftpFileNodeProps) {
    function handleToggle() {
        if (!disabled) {
            onToggleFile(file);
        }
    }

    return (
        <ListItemButton onClick={handleToggle} sx={{ pl: 1 + depth * 2, py: 0.25, opacity: disabled ? 0.4 : 1 }} data-sftp-name={file.Name}>
            <ListItemIcon sx={{ minWidth: 24 }} />
            <Checkbox
                size="small"
                checked={selectedFiles.has(file.Key)}
                disabled={disabled}
                onChange={handleToggle}
                onClick={(e) => e.stopPropagation()}
                sx={{ p: 0.5, mr: 0.5 }}
            />
            <ListItemIcon sx={{ minWidth: 28 }}>
                <InsertDriveFileIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
                primary={file.Name}
                secondary={`${formatSize(file.Size)} · ${formatDate(file.LastModified)}`}
                primaryTypographyProps={{ variant: 'body2' }}
                secondaryTypographyProps={{ variant: 'caption' }}
            />
        </ListItemButton>
    );
}

interface ISftpFolderNodeProps {
    relativePath: string;
    name: string;
    projectToken: string;
    listFiles: (variables: { path: string }) => Promise<IS3ListFilesResponse>;
    depth: number;
    queueType: QueueType;
    selectedFiles: Map<string, IS3File>;
    onToggleFile: (file: IS3File) => void;
    onToggleFiles: (files: IS3File[], checked: boolean) => void;
    showHidden?: boolean;
}

function SftpFolderNode({ relativePath, name, projectToken, listFiles, depth, queueType, selectedFiles, onToggleFile, onToggleFiles, showHidden }: ISftpFolderNodeProps) {
    const [expanded, setExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [content, setContent] = useState<IS3ListFilesResponse | null>(null);

    const handler = getQueueTypeHandler(queueType);
    const directFiles = content?.Files ?? [];
    const validFiles = directFiles.filter((f) => handler.validateFileExtension(f.Name));
    const selectedCount = validFiles.filter((f) => selectedFiles.has(f.Key)).length;
    const folderChecked = validFiles.length > 0 && selectedCount === validFiles.length;
    const folderIndeterminate = selectedCount > 0 && selectedCount < validFiles.length;

    const handleToggle = async () => {
        if (!expanded && content === null) {
            setLoading(true);
            try {
                const result = await listFiles({ path: relativePath });
                setContent(result);
            } finally {
                setLoading(false);
            }
        }
        setExpanded((prev) => !prev);
    };

    const handleFolderCheckbox = async (e: React.MouseEvent) => {
        e.stopPropagation();
        let files = content?.Files ?? [];
        if (content === null) {
            setLoading(true);
            try {
                const result = await listFiles({ path: relativePath });
                setContent(result);
                setExpanded(true);
                files = result.Files;
            } finally {
                setLoading(false);
            }
        }
        const valid = files.filter((f) => handler.validateFileExtension(f.Name));
        const allSelected = valid.length > 0 && valid.every((f) => selectedFiles.has(f.Key));
        onToggleFiles(valid, !allSelected);
    };

    return (
        <Fragment>
            <ListItemButton onClick={handleToggle} sx={{ pl: 1 + depth * 2, py: 0.5 }} data-sftp-name={name}>
                <ListItemIcon sx={{ minWidth: 24 }}>
                    {loading ? (
                        <CircularProgress size={16} />
                    ) : expanded ? (
                        <IndeterminateCheckBoxOutlinedIcon fontSize="small" sx={{ color: '#9e9e9e' }} />
                    ) : (
                        <AddBoxOutlinedIcon fontSize="small" sx={{ color: '#9e9e9e' }} />
                    )}
                </ListItemIcon>
                <Checkbox
                    size='small'
                    checked={folderChecked}
                    indeterminate={folderIndeterminate}
                    onClick={handleFolderCheckbox}
                    onChange={() => {}}
                    sx={{ p: 0.5, mr: 0.5 }}
                />
                <ListItemIcon sx={{ minWidth: 28 }}>
                    {expanded ? (
                        <FolderOpenIcon fontSize="small" color="warning" />
                    ) : (
                        <FolderIcon fontSize="small" color="warning" />
                    )}
                </ListItemIcon>
                <ListItemText
                    primary={name}
                    secondary={(() => {
                        if (content === null) {
                            return undefined;
                        }
                        const hiddenCount = content.Files.filter((f) => !handler.validateFileExtension(f.Name)).length;
                        const hiddenLabel = hiddenCount > 0 ? ` - ${hiddenCount} hidden files` : '';
                        return `${content.Folders.length} folders · ${content.Files.length - hiddenCount} accepted files${hiddenLabel}`;
                    })()}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                />
            </ListItemButton>

            <Collapse in={expanded && content !== null} unmountOnExit>
                <List disablePadding>
                    {content?.Folders.map((folderPath) => {
                        return (
                            <SftpFolderNode
                                key={folderPath}
                                relativePath={`${folderPath}/`}
                                name={getFolderName(folderPath)}
                                projectToken={projectToken}
                                listFiles={listFiles}
                                depth={depth + 1}
                                queueType={queueType}
                                selectedFiles={selectedFiles}
                                onToggleFile={onToggleFile}
                                onToggleFiles={onToggleFiles}
                                showHidden={showHidden}
                            />
                        );
                    })}
                    {content?.Files.filter((f) => showHidden || handler.validateFileExtension(f.Name)).map((file) => {
                        const valid = handler.validateFileExtension(file.Name);
                        return (
                            <SftpFileNode
                                key={file.Key}
                                file={file}
                                depth={depth + 1}
                                selectedFiles={selectedFiles}
                                onToggleFile={valid ? onToggleFile : () => {}}
                                disabled={!valid}
                            />
                        );
                    })}
                    {content?.Folders.length === 0 && content?.Files.length === 0 && (
                        <ListItem sx={{ pl: 2 + (depth + 1) * 2 }}>
                            <ListItemText
                                primary="Empty folder"
                                primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                            />
                        </ListItem>
                    )}
                </List>
            </Collapse>
        </Fragment>
    );
}

interface IProps {
    projectToken?: string;
    queueType: QueueType;
    onFilesChange?: (files: IWizardFile[]) => void;
}

export function QueueProcessingSftpPath({ projectToken = '', queueType, onFilesChange }: IProps) {
    const [refreshKey, setRefreshKey] = useState(0);
    const [rootLoading, setRootLoading] = useState(false);
    const [rootContent, setRootContent] = useState<IS3ListFilesResponse | null>(null);
    const [selectedFiles, setSelectedFiles] = useState<Map<string, IS3File>>(new Map());
    const [showHidden, setShowHidden] = useState(false);
    const [searchText, setSearchText] = useState('');
    const treeContainerRef = useRef<HTMLDivElement>(null);
    const { mutateAsync: listFiles } = useListS3Files();

    useEffect(() => {
        setRootLoading(true);
        setRootContent(null);
        listFiles({ path: '' })
            .then((result) => setRootContent(result))
            .finally(() => setRootLoading(false));
    }, [refreshKey]);

    useEffect(() => {
        if (!searchText.trim() || !treeContainerRef.current) {
            return;
        }
        const lower = searchText.toLowerCase();
        const textNodes = treeContainerRef.current.querySelectorAll('[data-sftp-name]');
        let bestEl: Element | null = null;
        let bestScore = -1;

        textNodes.forEach((el) => {
            const name = (el.getAttribute('data-sftp-name') ?? '').toLowerCase();
            const idx = name.indexOf(lower);

            if (idx === -1) {
                return;
            }

            const score = idx === 0 ? 1000 - lower.length : 500 - idx;

            if (score > bestScore) {
                bestScore = score;
                bestEl = el;
            }
        });

        if (bestEl) {
            (bestEl as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [searchText]);

    const notifyChange = (next: Map<string, IS3File>) => {
        onFilesChange?.(Array.from(next.values()).map((f) => ({
            name: f.Name,
            path: f.Key,
            size: f.Size,
            lastModified: f.LastModified,
        })));
    };

    const handleToggleFile = (file: IS3File) => {
        const next = new Map(selectedFiles);
        if (next.has(file.Key)) {
            next.delete(file.Key);
        } else {
            next.set(file.Key, file);
        }
        setSelectedFiles(next);
        notifyChange(next);
    };

    const handleToggleFiles = (files: IS3File[], checked: boolean) => {
        const next = new Map(selectedFiles);
        for (const file of files) {
            if (checked) {
                next.set(file.Key, file);
            } else {
                next.delete(file.Key);
            }
        }
        setSelectedFiles(next);
        notifyChange(next);
    };

    const handleRefresh = () => {
        setSelectedFiles(new Map());
        setRefreshKey((k) => k + 1);
        onFilesChange?.([]);
    };

    return (
        <Box css={sftpPathStyle}>
            <Box className='sftp-header'>
                <Typography variant="subtitle2" color="text.secondary">
                    SFTP Files
                    {selectedFiles.size > 0 && (
                        <Typography component='span' variant='caption' sx={{ ml: 1, color: '#1565c0', fontWeight: 700 }}>
                            ({selectedFiles.size} selected)
                        </Typography>
                    )}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                    <TextField
                        size='small'
                        placeholder='Search...'
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <SearchIcon sx={{ fontSize: 16, color: '#9e9e9e' }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ width: 200, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: 12 } }}
                    />
                    <FormControlLabel
                        control={<Checkbox size='small' checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} sx={{ p: 0.5 }} />}
                        label={<Typography variant='caption' sx={{ color: '#757575' }}>Show hidden</Typography>}
                        sx={{ mr: 0 }}
                    />
                    <Button size="small" startIcon={<RefreshIcon />} onClick={handleRefresh}>
                        Refresh
                    </Button>
                </Box>
            </Box>

            <Paper variant="outlined" className='sftp-tree-container' ref={treeContainerRef}>
                {rootLoading && (
                    <Box className='sftp-tree-loading'>
                        <CircularProgress />
                    </Box>
                )}
                {!rootLoading && rootContent !== null && rootContent.Folders.length === 0 && rootContent.Files.length === 0 && (
                    <Box className='sftp-tree-empty'>
                        <Typography variant="body2" color="text.secondary">
                            No files found.
                        </Typography>
                    </Box>
                )}
                {!rootLoading && rootContent !== null && (rootContent.Folders.length > 0 || rootContent.Files.length > 0) && (
                    <List dense disablePadding>
                        {rootContent.Folders.map((folderPath) => (
                            <SftpFolderNode
                                key={folderPath}
                                relativePath={`${folderPath}/`}
                                name={getFolderName(folderPath)}
                                projectToken={projectToken}
                                listFiles={listFiles}
                                depth={0}
                                queueType={queueType}
                                selectedFiles={selectedFiles}
                                onToggleFile={handleToggleFile}
                                onToggleFiles={handleToggleFiles}
                                showHidden={showHidden}
                            />
                        ))}
                        {rootContent.Files.filter((f) => showHidden || getQueueTypeHandler(queueType).validateFileExtension(f.Name)).map((file) => {
                            const valid = getQueueTypeHandler(queueType).validateFileExtension(file.Name);
                            return (
                                <SftpFileNode
                                    key={file.Key}
                                    file={file}
                                    depth={0}
                                    selectedFiles={selectedFiles}
                                    onToggleFile={valid ? handleToggleFile : () => {}}
                                    disabled={!valid}
                                />
                            );
                        })}
                    </List>
                )}
            </Paper>
        </Box>
    );
}
